import { type ButtonHTMLAttributes, forwardRef } from "react";

/**
 * The primary-action primitive.
 *
 * Before this existed, a filled primary button was a hand-copied utility
 * string: 247 of them across 182 files in 164 distinct spellings, differing
 * only in padding, an arbitrary `text-[13px]`, whether the focus ring carried
 * an offset, and whether hover faded the fill or the whole element (label
 * included). `pnpm check:button-primitive` now ratchets that count downward.
 *
 * Deliberately NOT a client component — it has no state and no handlers of its
 * own, so it stays usable from React Server Components. Roughly half the call
 * sites are `<Link>`, not `<button>`; those consume `buttonClass()` directly:
 *
 *   <Link href={href} className={buttonClass({ size: "lg", shape: "pill" })}>
 *
 * Values follow DESIGN.md: one radius per role, flat at rest, hover shifts the
 * fill rather than the element's opacity, and the focus ring is always offset
 * from the background.
 */

export type ButtonVariant = "primary" | "secondary" | "ghost";
export type ButtonSize = "xs" | "sm" | "md" | "lg" | "xl";
/** Radius role, per DESIGN.md's Radius-Per-Role Rule. */
export type ButtonShape = "md" | "lg" | "xl" | "pill";

export type ButtonStyleProps = {
	variant?: ButtonVariant;
	size?: ButtonSize;
	shape?: ButtonShape;
	fullWidth?: boolean;
	className?: string;
};

const BASE =
	"cursor-pointer items-center justify-center gap-2 font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:cursor-not-allowed disabled:opacity-50";

/**
 * Full-width buttons use block-level `flex`, not `inline-flex`: an inline-level
 * box sits on a text baseline, which leaves a few px of descender space under
 * it inside a block parent.
 */
const DISPLAY = { auto: "inline-flex", full: "flex w-full" } as const;

const VARIANT: Record<ButtonVariant, string> = {
	primary: "bg-primary text-primary-foreground hover:bg-primary/90",
	secondary:
		"border border-border bg-secondary text-secondary-foreground hover:bg-muted",
	ghost: "text-foreground hover:bg-muted",
};

const SIZE: Record<ButtonSize, string> = {
	xs: "px-3 py-1.5 text-xs",
	sm: "px-4 py-2 text-sm",
	md: "px-6 py-2.5 text-sm",
	lg: "px-6 py-3 text-base",
	xl: "px-8 py-3 text-base",
};

const SHAPE: Record<ButtonShape, string> = {
	md: "rounded-md",
	lg: "rounded-lg",
	xl: "rounded-xl",
	pill: "rounded-full",
};

/**
 * The class string for a button-shaped element. Use on `<Link>`, `<a>`, or any
 * host element that is not a `<button>`; use the `Button` component when it is.
 */
export function buttonClass({
	variant = "primary",
	size = "md",
	shape = "lg",
	fullWidth = false,
	className = "",
}: ButtonStyleProps = {}): string {
	return [
		DISPLAY[fullWidth ? "full" : "auto"],
		BASE,
		VARIANT[variant],
		SIZE[size],
		SHAPE[shape],
		className,
	]
		.filter(Boolean)
		.join(" ");
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
	ButtonStyleProps;

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
	function Button(
		{ variant, size, shape, fullWidth, className, type = "button", ...rest },
		ref,
	) {
		return (
			<button
				ref={ref}
				type={type}
				className={buttonClass({ variant, size, shape, fullWidth, className })}
				{...rest}
			/>
		);
	},
);
